// @mui
import { Theme, useTheme } from '@mui/material/styles';
import {
  Box,
  SxProps,
  Checkbox,
  TableRow,
  TableCell,
  TableHead,
  TableSortLabel,
  Stack,
} from '@mui/material';
import { FeatureBadge } from "../subscription/AccountBadge";
import CustomTooltip from '../Tooltip';

// ----------------------------------------------------------------------

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
} as const;

// ----------------------------------------------------------------------

type Props = {
  order?: 'asc' | 'desc';
  orderBy?: string;
  headLabel: any[];
  rowCount?: number;
  numSelected?: number;
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
  sx?: SxProps<Theme>;
};

export default function TableHeadCustom({
  order,
  orderBy,
  rowCount = 0,
  headLabel,
  numSelected = 0,
  onSort,
  onSelectAllRows,
  sx,
}: Props) {
  const theme = useTheme();
  
  return (
    <TableHead sx={{ ...sx, '& .MuiTableCell-head': {
      boxShadow: 'none !important',
      borderRadius: '0 !important',
      py: 2.5,
      color: 'inherit',
      typography: 'h4',
      fontSize: '16px !important',
      backgroundColor: (theme) => theme.palette.branded.paper,
    } }}>
      <TableRow>
        {onSelectAllRows && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onSelectAllRows(event.target.checked)
              }
            />
          </TableCell>
        )}

        {headLabel.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ width: headCell?.width, p: 1.5 }}
          >
            {headCell?.order && onSort ? (
              <TableSortLabel
                hideSortIcon
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => onSort(headCell.id)}
              >
                {headCell.label}

                {orderBy === headCell.id ? (
                  <Box sx={{ ...visuallyHidden }}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
                <Stack spacing={1} direction={'row'} flexWrap={'wrap'} alignItems={'center'} justifyContent={headCell.align || 'left'}>
                  <Stack spacing={0.5}>
                    {headCell.label && <Box>{headCell.label}</Box>}
                    {headCell.featureBadgeStatus && <FeatureBadge status={headCell.featureBadgeStatus}/>}
                  </Stack>
                  {headCell?.tooltip && (
                    <CustomTooltip title={headCell?.tooltip} bg={theme?.palette.branded.carbon} />
                  )}
                </Stack>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
